import React, {useEffect, useState, useCallback} from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Divider,
    Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MenuApi from 'src/api/menuApi/menuApi';
import {useDispatch} from 'react-redux';
import {showNotification} from 'src/redux/reducers/notificationReducer';
import {showConfirm} from 'src/redux/reducers/confirmReducer';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import { debounce } from 'lodash';

import RichTextEditor from 'src/components/RichTextEditor';

// Debounced input component to prevent re-renders on every keystroke
const DebouncedTextField = ({ value, onChange, ...props }: any) => {
    const [innerValue, setInnerValue] = useState(value);

    useEffect(() => {
        setInnerValue(value);
    }, [value]);

    const debouncedOnChange = useCallback(
        debounce((newValue) => onChange(newValue), 300),
        [onChange]
    );

    const handleTextChange = (e: any) => {
        setInnerValue(e.target.value);
        debouncedOnChange(e.target.value);
    };

    return <TextField {...props} value={innerValue} onChange={handleTextChange} />;
};

const pageValidationSchema = Yup.object({
    isim: Yup.string().required('Sayfa ismi zorunludur'),
    kategori: Yup.number().required('Kategori seçimi zorunludur'),
});

const productValidationSchema = Yup.object({
    isim: Yup.string().required('Ürün ismi zorunludur'),
    fiyat: Yup.number().required('Fiyat zorunludur').min(0, 'Fiyat 0\'dan küçük olamaz'),
    fiyat_2: Yup.number().min(0, 'Fiyat 2 0\'dan küçük olamaz'),
    sayfa: Yup.mixed().required('Sayfa seçimi zorunludur'),
    sayfa_sira: Yup.number().required('Sıra numarası zorunludur'),
});

const MenuYonetimi = () => {
    const dispatch = useDispatch();
    const [menuData, setMenuData] = useState<any[]>([]);

    const [draggedUrun, setDraggedUrun] = useState<any>(null);

    const [changes, setChanges] = useState({
        sayfalar: {eklenenler: [] as any[], guncellenenler: [] as any[], silinenler: [] as number[]},
        urunler: {eklenenler: [] as any[], guncellenenler: [] as any[], silinenler: [] as number[]}
    });

    const [past, setPast] = useState<any[]>([]);
    const [future, setFuture] = useState<any[]>([]);

    const recordHistory = (newMenuData: any[], newChanges: any) => {
        setPast(prev => [...prev, {menuData, changes}]);
        setFuture([]);
        setMenuData(newMenuData);
        setChanges(newChanges);
    };

    const handleUndo = () => {
        if (past.length === 0) return;
        const previous = past[past.length - 1];
        const newPast = past.slice(0, past.length - 1);
        setFuture(prev => [{menuData, changes}, ...prev]);
        setPast(newPast);
        setMenuData(previous.menuData);
        setChanges(previous.changes);
    };

    const handleRedo = () => {
        if (future.length === 0) return;
        const next = future[0];
        const newFuture = future.slice(1);
        setPast(prev => [...prev, {menuData, changes}]);
        setFuture(newFuture);
        setMenuData(next.menuData);
        setChanges(next.changes);
    };

    const [openSayfaModal, setOpenSayfaModal] = useState(false);
    const [openUrunModal, setOpenUrunModal] = useState(false);

    const pageFormik = useFormik({
        initialValues: {
            id: undefined,
            tempId: undefined,
            isim: '',
            sayfa_metni: '',
            kategori: '',
            fiyat1: '',
            fiyat2: ''
        },
        validationSchema: pageValidationSchema,
        onSubmit: (values) => {
            let nextMenuData = [...menuData];
            const nextChanges = JSON.parse(JSON.stringify(changes));

            if (values.tempId || values.id) {
                const cleaned = nextMenuData.map(k => ({
                    ...k,
                    sayfalar: k.sayfalar.filter((s: any) => s.id !== values.id && (!s.tempId || s.tempId !== values.tempId))
                }));
                nextMenuData = cleaned.map(k => k.id === values.kategori ? {
                    ...k,
                    sayfalar: [...k.sayfalar, values]
                } : k);

                if (values.id) {
                    nextChanges.sayfalar.guncellenenler = [...nextChanges.sayfalar.guncellenenler.filter((s: any) => s.id !== values.id), values];
                } else {
                    nextChanges.sayfalar.eklenenler = nextChanges.sayfalar.eklenenler.map((s: any) => s.tempId === values.tempId ? values : s);
                }
            } else {
                const tempId = Date.now();
                const newSayfa = {...values, tempId, urunler: []};
                nextMenuData = nextMenuData.map(k => k.id === values.kategori ? {
                    ...k,
                    sayfalar: [...k.sayfalar, newSayfa]
                } : k);
                nextChanges.sayfalar.eklenenler.push(newSayfa);
            }

            recordHistory(nextMenuData, nextChanges);
            setOpenSayfaModal(false);
        }
    });

    const productFormik = useFormik({
        initialValues: {
            id: undefined,
            tempId: undefined,
            isim: '',
            fiyat: 0,
            fiyat_2: 0,
            ozel_fiyat: 0,
            aciklama: '',
            icindekiler: '',
            alerjenler: '',
            mevcut: true,
            sayfa_sira: 0,
            tarif: '',
            sayfa: ''
        },
        validationSchema: productValidationSchema,
        onSubmit: (values) => {
            let nextMenuData = [...menuData];
            const nextChanges = JSON.parse(JSON.stringify(changes));

            if (values.tempId || values.id) {
                const cleaned = nextMenuData.map(k => ({
                    ...k,
                    sayfalar: k.sayfalar.map((s: any) => ({
                        ...s,
                        urunler: s.urunler.filter((u: any) => u.id !== values.id && (!u.tempId || u.tempId !== values.tempId))
                    }))
                }));
                nextMenuData = cleaned.map(k => ({
                    ...k,
                    sayfalar: k.sayfalar.map((s: any) => (s.id === values.sayfa || (s.tempId && s.tempId === values.sayfa))
                        ? {...s, urunler: [...s.urunler, values]}
                        : s
                    )
                }));

                if (values.id) {
                    nextChanges.urunler.guncellenenler = [...nextChanges.urunler.guncellenenler.filter((u: any) => u.id !== values.id), values];
                } else {
                    nextChanges.urunler.eklenenler = nextChanges.urunler.eklenenler.map((u: any) => u.tempId === values.tempId ? values : u);
                }
            } else {
                const tempId = Date.now();
                const newUrun = {...values, tempId};
                nextMenuData = nextMenuData.map(k => ({
                    ...k,
                    sayfalar: k.sayfalar.map((s: any) => (s.id === values.sayfa || (s.tempId && s.tempId === values.sayfa)) ? {
                        ...s,
                        urunler: [...s.urunler, newUrun]
                    } : s)
                }));
                nextChanges.urunler.eklenenler.push(newUrun);
            }

            recordHistory(nextMenuData, nextChanges);
            setOpenUrunModal(false);
        }
    });

    const fetchData = async () => {
        try {
            const data = await MenuApi.getFullMenu();
            const autoChanges: any[] = [];
            const normalizedData = data.filter((k: any) => k.isim !== "Kahve Çekirdekleri").map((kategori: any) => ({
                ...kategori,
                sayfalar: kategori.sayfalar.map((sayfa: any) => {
                    const sortedUrunler = [...sayfa.urunler].sort((a, b) => (a.sayfa_sira || 0) - (b.sayfa_sira || 0) || (a.fiyat - b.fiyat));
                    let pageChanged = false;
                    const reorderedUrunler = sortedUrunler.map((urun, index) => {
                        const newSira = index + 1;
                        if (urun.sayfa_sira !== newSira) {
                            pageChanged = true;
                            const updatedUrun = {...urun, sayfa_sira: newSira, sayfa: sayfa.id};
                            autoChanges.push(updatedUrun);
                            return updatedUrun;
                        }
                        return urun;
                    });
                    return pageChanged ? {...sayfa, urunler: reorderedUrunler} : sayfa;
                })
            }));

            setMenuData(normalizedData);
            setChanges({
                sayfalar: {eklenenler: [], guncellenenler: [], silinenler: []},
                urunler: {eklenenler: [], guncellenenler: autoChanges, silinenler: []}
            });
            setPast([]);
            setFuture([]);
        } catch (error) {
            console.error("Veri çekme hatası:", error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleDragStart = (e: React.DragEvent, urun: any, sourceSayfaId: any) => {
        setDraggedUrun({...urun, sourceSayfaId});
        e.dataTransfer.setData('text/plain', '');
    };

    const handleDrop = (e: React.DragEvent, targetSayfaId: any, targetUrunId?: any) => {
        e.preventDefault();
        if (!draggedUrun) return;

        let nextMenuData = JSON.parse(JSON.stringify(menuData));
        const nextChanges = JSON.parse(JSON.stringify(changes));

        nextMenuData = nextMenuData.map((k: any) => ({
            ...k,
            sayfalar: k.sayfalar.map((s: any) => ({
                ...s,
                urunler: s.urunler.filter((u: any) => (u.id || u.tempId) !== (draggedUrun.id || draggedUrun.tempId))
            }))
        }));

        const updatedUrun = {...draggedUrun, sayfa: targetSayfaId};
        delete updatedUrun.sourceSayfaId;

        nextMenuData = nextMenuData.map((k: any) => ({
            ...k,
            sayfalar: k.sayfalar.map((s: any) => {
                if ((s.id || s.tempId) === targetSayfaId) {
                    const newUrunler = [...s.urunler];
                    if (targetUrunId) {
                        const targetIdx = newUrunler.findIndex(u => (u.id || u.tempId) === targetUrunId);
                        newUrunler.splice(targetIdx, 0, updatedUrun);
                    } else {
                        newUrunler.push(updatedUrun);
                    }
                    return {...s, urunler: newUrunler};
                }
                return s;
            })
        }));

        const affectedPageIds = [draggedUrun.sourceSayfaId, targetSayfaId];
        nextMenuData = nextMenuData.map((k: any) => ({
            ...k,
            sayfalar: k.sayfalar.map((s: any) => {
                if (affectedPageIds.includes(s.id || s.tempId)) {
                    const reorderedUrunler = s.urunler.map((u: any, idx: number) => {
                        const newSira = idx + 1;
                        if (u.sayfa_sira !== newSira || u.sayfa !== (s.id || s.tempId)) {
                            const updatedU = {...u, sayfa_sira: newSira, sayfa: s.id || s.tempId};
                            if (updatedU.id) {
                                nextChanges.urunler.guncellenenler = [
                                    ...nextChanges.urunler.guncellenenler.filter((c: any) => c.id !== updatedU.id),
                                    updatedU
                                ];
                            } else {
                                nextChanges.urunler.eklenenler = nextChanges.urunler.eklenenler.map((c: any) =>
                                    c.tempId === updatedU.tempId ? updatedU : c
                                );
                            }
                            return updatedU;
                        }
                        return u;
                    });
                    return {...s, urunler: reorderedUrunler};
                }
                return s;
            })
        }));

        recordHistory(nextMenuData, nextChanges);
        setDraggedUrun(null);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleSayfaDelete = (sayfa: any) => {
        dispatch(showConfirm({
            message: "Bu sayfayı silmek istediğinize emin misiniz?",
            onConfirm: () => {
                const nextMenuData = menuData.map(k => ({
                    ...k,
                    sayfalar: k.sayfalar.filter((s: any) => s.id !== sayfa.id && (!s.tempId || s.tempId !== sayfa.tempId))
                }));
                const nextChanges = JSON.parse(JSON.stringify(changes));
                if (sayfa.id) {
                    nextChanges.sayfalar.silinenler.push(sayfa.id);
                    nextChanges.sayfalar.guncellenenler = nextChanges.sayfalar.guncellenenler.filter((s: any) => s.id !== sayfa.id);
                } else {
                    nextChanges.sayfalar.eklenenler = nextChanges.sayfalar.eklenenler.filter((s: any) => s.tempId !== sayfa.tempId);
                }
                recordHistory(nextMenuData, nextChanges);
            }
        }));
    };

    const handleUrunDelete = (urun: any) => {
        dispatch(showConfirm({
            message: "Bu ürünü silmek istediğinize emin misiniz?",
            onConfirm: () => {
                const nextMenuData = menuData.map(k => ({
                    ...k,
                    sayfalar: k.sayfalar.map((s: any) => ({
                        ...s,
                        urunler: s.urunler.filter((u: any) => u.id !== urun.id && (!u.tempId || u.tempId !== urun.tempId))
                    }))
                }));
                const nextChanges = JSON.parse(JSON.stringify(changes));
                if (urun.id) {
                    nextChanges.urunler.silinenler.push(urun.id);
                    nextChanges.urunler.guncellenenler = nextChanges.urunler.guncellenenler.filter((u: any) => u.id !== urun.id);
                } else {
                    nextChanges.urunler.eklenenler = nextChanges.urunler.eklenenler.filter((u: any) => u.tempId !== urun.tempId);
                }
                recordHistory(nextMenuData, nextChanges);
            }
        }));
    };

    const handleUpdateAll = async () => {
        try {
            await MenuApi.topluGuncelle(changes);
            dispatch(showNotification({message: "Menü başarıyla güncellendi.", severity: "success"}));
            fetchData();
        } catch (error) {
            console.error("Toplu güncelleme hatası:", error);
        }
    };

    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                p: 1,
                bgcolor: '#f8f9fa',
                borderRadius: 1
            }}>
                <Stack direction="row" spacing={1} sx={{mr: 'auto'}}>
                    <Tooltip title="Geri Al">
                        <span><IconButton onClick={handleUndo} disabled={past.length === 0} color="primary"><UndoIcon/></IconButton></span>
                    </Tooltip>
                    <Tooltip title="İleri Al">
                        <span><IconButton onClick={handleRedo} disabled={future.length === 0}
                                          color="primary"><RedoIcon/></IconButton></span>
                    </Tooltip>
                </Stack>
                <Button variant="contained" color="primary" sx={{minWidth: 200, height: 40, fontWeight: 'bold'}}
                        startIcon={<SaveIcon/>} onClick={handleUpdateAll}>Tüm Değişiklikleri Kaydet</Button>
            </Box>

            {menuData.map((kategori) => (
                <Accordion key={kategori.id} sx={{mb: 1}}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{width: '100%'}}>
                            <Typography variant="h6" sx={{flexGrow: 1}}>{kategori.isim}</Typography>
                            <Box onClick={(e) => e.stopPropagation()}>
                                <Tooltip title="Sayfa Ekle">
                                    <IconButton size="small" color="primary" onClick={() => {
                                        pageFormik.setValues({
                                            id: undefined,
                                            tempId: undefined,
                                            isim: '',
                                            sayfa_metni: '',
                                            kategori: kategori.id,
                                            fiyat1: '',
                                            fiyat2: ''
                                        });
                                        setOpenSayfaModal(true);
                                    }}><AddIcon/></IconButton>
                                </Tooltip>
                            </Box>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        {kategori.sayfalar && kategori.sayfalar.length > 0 ? (
                            kategori.sayfalar.map((sayfa: any) => (
                                <Accordion key={sayfa.id || sayfa.tempId} sx={{mb: 1, border: '1px solid #eee'}}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                        <Stack direction="row" spacing={2} alignItems="center" sx={{width: '100%'}}>
                                            <Typography variant="subtitle1" sx={{flexGrow: 1}}>{sayfa.isim}</Typography>
                                            <Box onClick={(e) => e.stopPropagation()}>
                                                <Tooltip title="Ürün Ekle">
                                                    <IconButton size="small" color="primary" onClick={() => {
                                                        productFormik.setValues({
                                                            id: undefined,
                                                            tempId: undefined,
                                                            isim: '',
                                                            fiyat: 0,
                                                            fiyat_2: 0,
                                                            ozel_fiyat: 0,
                                                            aciklama: '',
                                                            icindekiler: '',
                                                            alerjenler: '',
                                                            mevcut: true,
                                                            sayfa_sira: (sayfa.urunler?.length || 0) + 1,
                                                            tarif: '',
                                                            sayfa: sayfa.id || sayfa.tempId
                                                        });
                                                        setOpenUrunModal(true);
                                                    }}><AddIcon/></IconButton>
                                                </Tooltip>
                                                <IconButton size="small" onClick={() => {
                                                    pageFormik.setValues({...sayfa, kategori: kategori.id});
                                                    setOpenSayfaModal(true);
                                                }}><EditIcon/></IconButton>
                                                <IconButton size="small" color="error"
                                                            onClick={() => handleSayfaDelete(sayfa)}><DeleteIcon/></IconButton>
                                            </Box>
                                        </Stack>
                                    </AccordionSummary>
                                    <AccordionDetails onDragOver={handleDragOver}
                                                      onDrop={(e) => handleDrop(e, sayfa.id || sayfa.tempId)}>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{bgcolor: '#f5f5f5'}}>
                                                        <TableCell width={40}></TableCell>
                                                        <TableCell>Ürün Adı</TableCell>
                                                        <TableCell align="right">{sayfa.fiyat1 || 'Fiyat'}</TableCell>
                                                        {sayfa.fiyat2 &&
                                                            <TableCell align="right">{sayfa.fiyat2}</TableCell>}
                                                        <TableCell align="right">İşlemler</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {sayfa.urunler && [...sayfa.urunler]
                                                        .sort((a, b) => (a.sayfa_sira || 0) - (b.sayfa_sira || 0) || (a.fiyat - b.fiyat))
                                                        .map((urun: any) => (
                                                            <TableRow
                                                                key={urun.id || urun.tempId} draggable
                                                                onDragStart={(e) => handleDragStart(e, urun, sayfa.id || sayfa.tempId)}
                                                                onDragOver={handleDragOver}
                                                                onDrop={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDrop(e, sayfa.id || sayfa.tempId, urun.id || urun.tempId);
                                                                }}
                                                                sx={{
                                                                    cursor: 'grab',
                                                                    '&:hover': {bgcolor: '#fdfdfd'},
                                                                    opacity: draggedUrun && (draggedUrun.id === urun.id && draggedUrun.tempId === urun.tempId) ? 0.5 : 1
                                                                }}
                                                            >
                                                                <TableCell><DragIndicatorIcon fontSize="small"
                                                                                              color="action"/></TableCell>
                                                                <TableCell>{urun.isim}</TableCell>
                                                                <TableCell align="right">{urun.fiyat}₺</TableCell>
                                                                {sayfa.fiyat2 && <TableCell
                                                                    align="right">{urun.fiyat_2 || '-'}₺</TableCell>}
                                                                <TableCell align="right">
                                                                    <IconButton size="small" onClick={() => {
                                                                        productFormik.setValues({
                                                                            ...urun,
                                                                            sayfa: sayfa.id || sayfa.tempId
                                                                        });
                                                                        setOpenUrunModal(true);
                                                                    }}><EditIcon fontSize="small"/></IconButton>
                                                                    <IconButton size="small" color="error"
                                                                                onClick={() => handleUrunDelete(urun)}><DeleteIcon
                                                                        fontSize="small"/></IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    {(!sayfa.urunler || sayfa.urunler.length === 0) && (
                                                        <TableRow><TableCell colSpan={6} align="center">Ürün
                                                            bulunamadı</TableCell></TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </AccordionDetails>
                                </Accordion>
                            ))
                        ) : (
                            <Typography color="textSecondary">Bu kategoride henüz sayfa yok.</Typography>
                        )}
                    </AccordionDetails>
                </Accordion>
            ))}

            <Dialog open={openSayfaModal} onClose={() => setOpenSayfaModal(false)} fullWidth maxWidth="sm">
                <form onSubmit={pageFormik.handleSubmit}>
                    <DialogTitle>{(pageFormik.values.id || pageFormik.values.tempId) ? 'Sayfa Düzenle' : 'Yeni Sayfa'}</DialogTitle>
                    <DialogContent>
                        <TextField select fullWidth label="Kategori" name="kategori" value={pageFormik.values.kategori}
                                   onChange={pageFormik.handleChange} onBlur={pageFormik.handleBlur}
                                   error={pageFormik.touched.kategori && Boolean(pageFormik.errors.kategori)}
                                   helperText={pageFormik.touched.kategori && pageFormik.errors.kategori} sx={{mt: 2}}>
                            {menuData.map((k) => (<MenuItem key={k.id} value={k.id}>{k.isim}</MenuItem>))}
                        </TextField>
                        <TextField fullWidth label="Sayfa Adı" name="isim" value={pageFormik.values.isim}
                                   onChange={pageFormik.handleChange} onBlur={pageFormik.handleBlur}
                                   error={pageFormik.touched.isim && Boolean(pageFormik.errors.isim)}
                                   helperText={pageFormik.touched.isim && pageFormik.errors.isim} sx={{mt: 2}}/>
                        <TextField fullWidth multiline rows={3} label="Sayfa Metni" name="sayfa_metni"
                                   value={pageFormik.values.sayfa_metni} onChange={pageFormik.handleChange}
                                   sx={{mt: 2}}/>
                        <Box sx={{display: 'flex', gap: 2, mt: 2}}>
                            <TextField fullWidth label="Fiyat 1 (Başlık)" name="fiyat1" value={pageFormik.values.fiyat1}
                                       onChange={pageFormik.handleChange}/>
                            <TextField fullWidth label="Fiyat 2 (Başlık)" name="fiyat2" value={pageFormik.values.fiyat2}
                                       onChange={pageFormik.handleChange}/>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenSayfaModal(false)}>İptal</Button>
                        <Button color="primary" type="submit">Taslağa Kaydet</Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Dialog open={openUrunModal} onClose={() => setOpenUrunModal(false)} fullWidth maxWidth="sm">
                <form onSubmit={productFormik.handleSubmit}>
                    <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', mb: 2 }}>
                        {(productFormik.values.id || productFormik.values.tempId) ? 'Ürün Düzenle' : 'Yeni Ürün'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 1 }}>
                            <TextField select fullWidth label="Sayfa" name="sayfa" value={productFormik.values.sayfa}
                                       onChange={productFormik.handleChange} onBlur={productFormik.handleBlur}
                                       error={productFormik.touched.sayfa && Boolean(productFormik.errors.sayfa)}
                                       helperText={productFormik.touched.sayfa && productFormik.errors.sayfa}>
                                {menuData.flatMap(k => k.sayfalar || []).map((s: any) => (
                                    <MenuItem key={s.id || s.tempId} value={s.id || s.tempId}>{s.isim}</MenuItem>))}
                            </TextField>
                            
                            <DebouncedTextField fullWidth label="Ürün Adı" name="isim" value={productFormik.values.isim}
                                       onChange={(val: any) => productFormik.setFieldValue('isim', val)}
                                       onBlur={productFormik.handleBlur}
                                       error={productFormik.touched.isim && Boolean(productFormik.errors.isim)}
                                       helperText={productFormik.touched.isim && productFormik.errors.isim} />

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField fullWidth type="number" label="Fiyat" name="fiyat"
                                           value={productFormik.values.fiyat} onChange={productFormik.handleChange}
                                           onBlur={productFormik.handleBlur}
                                           error={productFormik.touched.fiyat && Boolean(productFormik.errors.fiyat)}
                                           helperText={productFormik.touched.fiyat && productFormik.errors.fiyat}/>
                                <TextField fullWidth type="number" label="Fiyat 2" name="fiyat_2"
                                           value={productFormik.values.fiyat_2} onChange={productFormik.handleChange}
                                           onBlur={productFormik.handleBlur}
                                           error={productFormik.touched.fiyat_2 && Boolean(productFormik.errors.fiyat_2)}
                                           helperText={productFormik.touched.fiyat_2 && productFormik.errors.fiyat_2}/>
                            </Box>

                            <Divider sx={{ my: 1 }}>Açıklama ve Detaylar</Divider>

                            <RichTextEditor label="Açıklama" value={productFormik.values.aciklama}
                                            onChange={(html) => productFormik.setFieldValue('aciklama', html)} />
                            
                            <DebouncedTextField fullWidth multiline rows={2} label="İçindekiler" name="icindekiler"
                                       value={productFormik.values.icindekiler} 
                                       onChange={(val: any) => productFormik.setFieldValue('icindekiler', val)} />
                            
                            <DebouncedTextField fullWidth multiline rows={2} label="Alerjen Uyarıları" name="alerjenler"
                                       value={productFormik.values.alerjenler} 
                                       onChange={(val: any) => productFormik.setFieldValue('alerjenler', val)} />

                            <Divider sx={{ my: 1 }}>Hazırlanış (Sadece Personel Görür)</Divider>
                            
                            <RichTextEditor label="Tarif" value={productFormik.values.tarif}
                                            onChange={(html) => productFormik.setFieldValue('tarif', html)} />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button onClick={() => setOpenUrunModal(false)} variant="outlined">İptal</Button>
                        <Button color="primary" type="submit" variant="contained">Taslağa Kaydet</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default MenuYonetimi;
